import { useCallback, useEffect, useState } from 'react'
import { FileSignature, Loader2, Plus } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  criarTermoModelo, listTermoModelos, novaVersaoTermo, publicarTermoVersao,
  salvarConteudoRascunho, type TermoModeloComVersao, type TermoVersao,
} from '@/services/termos'

export default function TermosPage() {
  const { current, pode } = useAuth()
  const [modelos, setModelos] = useState<TermoModeloComVersao[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [novoNome, setNovoNome] = useState('')
  const [criando, setCriando] = useState(false)
  const [editando, setEditando] = useState<TermoModeloComVersao | null>(null)
  const [versao, setVersao] = useState<TermoVersao | null>(null)
  const [conteudo, setConteudo] = useState('')

  const podeGerir = pode('termos.gerir')

  const carregar = useCallback(async () => {
    if (!current) return
    setLoading(true)
    setErro(null)
    try {
      setModelos(await listTermoModelos(current.workspace.id))
    } catch {
      setErro('Não foi possível carregar. A migração phase6 já foi aplicada?')
    } finally {
      setLoading(false)
    }
  }, [current])

  useEffect(() => { carregar() }, [carregar])

  async function abrirEditor(m: TermoModeloComVersao) {
    if (!current) return
    let rascunho = m.rascunho
    if (!rascunho) rascunho = await novaVersaoTermo(current.workspace.id, m)
    setEditando(m)
    setVersao(rascunho)
    setConteudo(rascunho.conteudo)
  }

  async function criar(e: React.FormEvent) {
    e.preventDefault()
    if (!current || novoNome.trim().length < 3) return
    setBusy(true)
    try {
      await criarTermoModelo(current.workspace.id, novoNome)
      setCriando(false)
      setNovoNome('')
      await carregar()
    } catch {
      setErro('Não foi possível criar (verifique a permissão termos.gerir).')
    } finally {
      setBusy(false)
    }
  }

  async function salvar(publicar: boolean) {
    if (!current || !versao) return
    setBusy(true)
    setErro(null)
    try {
      await salvarConteudoRascunho(current.workspace.id, versao.id, conteudo)
      if (publicar) {
        await publicarTermoVersao(current.workspace.id, versao.id)
        setEditando(null)
      }
      await carregar()
    } catch {
      setErro('Não foi possível salvar.')
    } finally {
      setBusy(false)
    }
  }

  if (editando && versao) {
    return (
      <div>
        <h1 className="page-title" style={{ marginBottom: 4 }}>{editando.nome}</h1>
        <p className="page-subtitle" style={{ margin: '0 0 16px' }}>
          Editando rascunho v{versao.numero}. Depois de publicada, a versão fica congelada — assinaturas antigas nunca mudam.
        </p>
        <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <textarea
            className="field-input" rows={16} style={{ resize: 'vertical', fontSize: 14, lineHeight: 1.55 }}
            placeholder="Texto completo do termo/consentimento…"
            value={conteudo} onChange={e => setConteudo(e.target.value)}
            aria-label="Conteúdo do termo"
          />
          {erro && <div className="field-error" role="alert">{erro}</div>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn-primary" disabled={busy || conteudo.trim().length < 20} onClick={() => salvar(true)}>
              {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
              Publicar versão
            </button>
            <button className="btn-secondary" disabled={busy} onClick={() => salvar(false)}>Salvar rascunho</button>
            <button className="btn-secondary" disabled={busy} onClick={() => { setEditando(null); carregar() }}>Fechar</button>
          </div>
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    )
  }

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Termos e consentimentos</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Modelos versionados — o texto assinado nunca muda.</p>
        </div>
        {podeGerir && (
          <button className="btn-primary" onClick={() => setCriando(v => !v)}>
            <Plus size={16} aria-hidden /> Novo termo
          </button>
        )}
      </header>

      {criando && (
        <form onSubmit={criar} className="card" style={{ padding: 18, marginBottom: 14, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label className="field-label" htmlFor="nt-nome">Nome *</label>
            <input id="nt-nome" required className="field-input" value={novoNome} onChange={e => setNovoNome(e.target.value)} placeholder="Ex.: Consentimento para procedimento" />
          </div>
          <button type="submit" className="btn-primary" disabled={busy}>Criar</button>
        </form>
      )}

      {erro && !criando && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      ) : modelos.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <FileSignature size={26} aria-hidden style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Nenhum termo ainda</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {modelos.map(m => (
            <div key={m.id} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5 }}>{m.nome}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                  {m.versao_atual ? `v${m.versao_atual.numero} publicada` : 'nunca publicado'}
                  {m.rascunho ? ' · rascunho em edição' : ''}
                </div>
              </div>
              {m.versao_atual ? <span className="badge badge-ok">Ativo</span> : <span className="badge badge-warn">Rascunho</span>}
              {podeGerir && (
                <button className="btn-secondary" style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }} onClick={() => abrirEditor(m)}>
                  Editar
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
